import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Bell, LogOut, Truck, ChefHat, Ban } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import AccountStatusBanner from "./AccountStatusBanner";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

function relativeTime(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const diffSec = Math.floor((Date.now() - then) / 1000);
  if (diffSec < 45) return "adesso";
  if (diffSec < 90) return "1 min fa";
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 45) return `${diffMin} min fa`;
  if (diffMin < 90) return "1 h fa";
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} h fa`;
  if (diffH < 48) return "ieri";
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD} g fa`;
  return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    let m = true;
    const load = async () => {
      if (!user) return;
      try {
        const { data } = await api.get("/notifications");
        if (m) setNotifs(data);
      } catch (e) { if (process.env.NODE_ENV === "development") console.warn("notifications load failed:", e); }
    };
    load();
    const t = setInterval(load, 15000);
    return () => { m = false; clearInterval(t); };
  }, [user]);

  const unread = notifs.filter(n => !n.read).length;

  const markRead = async () => {
    setNotifs(prev => prev.map(n => ({ ...n, read: true })));
    try {
      await api.post("/notifications/mark-read");
    } catch (e) { console.warn("mark-read failed:", e); }
  };

  const doLogout = async () => { await logout(); navigate("/"); };

  const roleIcon = user?.role === "restaurant" ? ChefHat : Truck;
  const RoleIcon = roleIcon;

  const dashHref = user?.is_admin ? "/admin" : user?.role === "supplier" ? "/fornitore" : "/ristorante";
  const hideHeader = location.pathname === "/" && !user;

  return (
    <div className="min-h-screen flex flex-col">
      {!hideHeader && (
      <header className="no-print sticky top-0 z-30 border-b border-slate-200 bg-white/85 backdrop-blur-md">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to={user ? dashHref : "/"} className="flex items-center shrink-0 leading-none" data-testid="brand-home-link">
            <img src="/logo-h.jpg?v=5" alt="AstaAlimentare" className="h-11 sm:h-14 w-auto" />
          </Link>

          {user ? (
            <div className="flex items-center gap-2 sm:gap-3">
              <Badge variant="outline" className="hidden sm:inline-flex gap-1.5 border-slate-200 text-slate-700" data-testid="role-badge">
                <RoleIcon className="w-3.5 h-3.5" />
                {user.is_admin ? "Owner" : user.role === "restaurant" ? "Acquirente" : "Fornitore"}
              </Badge>

              <DropdownMenu onOpenChange={(o) => { if (o && unread) markRead(); }}>
                <DropdownMenuTrigger asChild>
                  <button className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors" data-testid="notifications-button">
                    <Bell className="w-5 h-5 text-slate-700" />
                    {unread > 0 && (
                      <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white flex items-center justify-center" style={{background: "var(--ga-primary)"}} data-testid="notifications-badge">
                        {unread}
                      </span>
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto">
                  <DropdownMenuLabel>Notifiche</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {notifs.length === 0 && (
                    <div className="px-3 py-6 text-sm text-slate-500 text-center">Nessuna notifica</div>
                  )}
                  {notifs.map(n => (
                    <DropdownMenuItem key={n.id} className="flex flex-col items-start gap-0.5 py-2 cursor-pointer" onClick={() => n.url ? navigate(n.url) : n.order_id && navigate(`/ordine/${n.order_id}`)} data-testid={`notif-${n.id}`}>
                      <div className="text-sm font-medium text-slate-800">{n.message}</div>
                      <div className="text-[11px] font-mono text-slate-500" title={new Date(n.created_at).toLocaleString("it-IT")}>
                        {relativeTime(n.created_at)}
                      </div>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors" data-testid="user-menu-button">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm text-white" style={{background: "var(--ga-primary)"}}>
                      {(user.business_name || user.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden md:inline text-sm font-medium max-w-[140px] truncate">{user.business_name || user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="font-semibold">{user.business_name || user.name}</div>
                    <div className="text-xs text-slate-500 font-normal">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(dashHref)} data-testid="menu-dashboard">{user.is_admin ? "Pannello owner" : "Dashboard"}</DropdownMenuItem>
                  {user.role === "restaurant" && !user.is_admin && <DropdownMenuItem onClick={() => navigate("/offerte")} data-testid="menu-offers">Offerte fornitori</DropdownMenuItem>}
                  {user.is_admin && <DropdownMenuItem onClick={() => navigate("/admin/contestazioni")} data-testid="menu-admin-disputes">Contestazioni</DropdownMenuItem>}
                  {user.is_admin && <DropdownMenuItem onClick={() => navigate("/ristorante?as=buyer")} data-testid="menu-admin-buyer-mode">Modalità acquirente (test)</DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={doLogout} className="text-red-600" data-testid="logout-button">
                    <LogOut className="w-4 h-4 mr-2" />Esci
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {location.pathname !== "/login" && (
                <Link to="/login"><Button variant="ghost" data-testid="header-login-button">Accedi</Button></Link>
              )}
              {location.pathname !== "/registrazione" && (
                <Link to="/registrazione"><Button className="ga-btn-primary" data-testid="header-register-button">Registrati</Button></Link>
              )}
            </div>
          )}
        </div>
      </header>
      )}

      <main className="flex-1">
        {user?.suspended_until && (
          <div className="bg-slate-900 text-white" data-testid="suspended-banner">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5 text-sm flex items-start gap-2">
              <Ban className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
              <div><b>Account sospeso</b> fino al {new Date(user.suspended_until).toLocaleString("it-IT")}{user.suspended_reason === "anti_elusione" && " per ripetuti segnali di elusione (clausola v1.1 / Condizioni)"}. Puoi consultare ordini e chat ma non ordinare, offrire o scrivere.{user.role === "restaurant" && " Puoi ancora pagare gli ordini già aggiudicati e annullare le aste aperte."} Per chiarimenti contatta l'amministratore.</div>
            </div>
          </div>
        )}
        <AccountStatusBanner />
        {children}
      </main>

      <footer className="no-print border-t border-slate-200 bg-white py-8 mt-12">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-sm text-slate-500">
            © {new Date().getFullYear()} AstaAlimentare — Aste inverse per la ristorazione
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono text-slate-400 uppercase tracking-widest">
            <Link to="/privacy" className="hover:text-slate-700" data-testid="footer-privacy-link">Privacy</Link>
            <Link to="/condizioni" className="hover:text-slate-700" data-testid="footer-terms-link">Condizioni</Link>
            <span>B2B Marketplace · Italia</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
